<section class="command-center">
  <div class="catalog-state" *ngIf="catalogLoading">
    <mat-spinner diameter="24"></mat-spinner><span>Loading device support capabilities...</span>
  </div>
  <div class="catalog-state catalog-state--error" *ngIf="catalogError" role="alert">
    <mat-icon>error_outline</mat-icon><span>{{ catalogError }}</span>
    <button mat-stroked-button type="button" (click)="retryCatalog()">Retry</button>
  </div>

  <mat-tab-group
    *ngIf="!catalogLoading"
    [(selectedIndex)]="activeTab"
    (selectedTabChange)="onCommandTabChanged($event)"
    animationDuration="150ms"
    class="command-tabs">
    <mat-tab label="Guided Troubleshooting" *ngIf="showTroubleshootTab">
      <div class="tab-content troubleshoot-layout">
        <section class="workspace-main">
          <div class="section-heading">
            <div>
              <h4>Start with the customer issue</h4>
              <p>Each workflow selects a safe, ordered diagnostic set. No configuration changes are included.</p>
            </div>
          </div>
          <div class="workflow-grid">
            <button
              type="button"
              class="workflow-card"
              *ngFor="let workflow of workflows"
              [class.workflow-card--active]="selectedWorkflow.id === workflow.id"
              (click)="activateWorkflow(workflow)">
              <mat-icon>{{ workflow.icon }}</mat-icon>
              <span>
                <strong>{{ workflow.name }}</strong>
                <small>{{ workflow.description }}</small>
              </span>
              <span class="workflow-card__meta">{{ workflow.toolIds.length }} checks</span>
            </button>
          </div>

          <section class="workflow-detail">
            <div>
              <span class="eyebrow">Selected workflow</span>
              <h4>{{ selectedWorkflow.name }}</h4>
              <p>{{ selectedWorkflow.description }}</p>
            </div>
            <div class="workflow-detail__body">
              <div>
                <strong>Common symptoms</strong>
                <ul><li *ngFor="let symptom of selectedWorkflow.symptoms">{{ symptom }}</li></ul>
              </div>
              <div>
                <strong>Execution order</strong>
                <ol>
                  <li *ngFor="let tool of selectedDiagnostics">{{ tool.name }}</li>
                </ol>
              </div>
            </div>
          </section>
        </section>

        <aside class="run-cart">
          <header><span>Selected checks</span><strong>{{ selectedDiagnosticCount }}</strong></header>
          <dl>
            <div><dt>Target</dt><dd>{{ diagnosticsTarget }}</dd></div>
            <div><dt>Interface</dt><dd>{{ selectedInterface || 'Required' }}</dd></div>
            <div><dt>Estimated time</dt><dd>{{ selectedEstimatedDuration }}s</dd></div>
            <div><dt>Risk</dt><dd><span class="risk-badge risk-badge--readonly">Read-only</span></dd></div>
          </dl>
          <button mat-raised-button color="primary" (click)="reviewWorkflow()"
            [disabled]="reviewLoading || !selectedDiagnosticCount || !selectedInterface">
            <mat-icon>fact_check</mat-icon>{{ reviewLoading ? 'Preparing...' : 'Review and run' }}
          </button>
          <button mat-button (click)="clearDiagnostics()" [disabled]="running">Clear selection</button>
        </aside>
      </div>
    </mat-tab>

    <mat-tab label="Network Utilities">
      <div class="tab-content diagnostics-page">
        <section class="diagnostics-utilities">
          <header class="section-heading">
            <div>
              <h4>Network utilities</h4>
              <p>Inspect network health or review a maintenance action. Nothing changes on the device until you confirm the generated commands.</p>
            </div>
          </header>

          <details class="support-accordion" open>
            <summary><span><mat-icon>monitor_heart</mat-icon><strong>Read-only diagnostics</strong><small>Check paths, throughput, QoS and link health without changing configuration.</small></span><span class="risk-badge risk-badge--readonly">Read-only</span></summary>
            <div class="utility-actions">
              <button mat-raised-button class="utility-command-button" (click)="openMtrInputs()" [disabled]="!canOpenDiagnostic('mtr')"><mat-icon>route</mat-icon>MTR</button>
              <button mat-raised-button class="utility-command-button" (click)="openSpeedTestInputs()" [disabled]="!canOpenDiagnostic('speed_test')"><mat-icon>speed</mat-icon>Speedtest</button>
              <button mat-raised-button class="utility-command-button" (click)="reviewDiagnosticShortcut('qos_statistics')" [disabled]="!isDiagnosticAvailable('qos_statistics') || running || reviewLoading"><mat-icon>tune</mat-icon>QoS</button>
              <button mat-raised-button class="utility-command-button" (click)="openIfstatInputs()" [disabled]="!canOpenDiagnostic('interface_statistics')"><mat-icon>show_chart</mat-icon>IfStats</button>
              <button mat-raised-button class="utility-command-button" (click)="reviewDiagnosticShortcut('link_statistics')" [disabled]="!isDiagnosticAvailable('link_statistics') || running || reviewLoading"><mat-icon>timeline</mat-icon>LinkStats</button>
              <button mat-raised-button class="utility-command-button" (click)="reviewDiagnosticShortcut('cellular_statistics')" [disabled]="!isDiagnosticAvailable('cellular_statistics') || running || reviewLoading"><mat-icon>signal_cellular_alt</mat-icon>4GLTE Stats</button>
              <button mat-raised-button class="utility-command-button" (click)="reviewDiagnosticShortcut('link_flaps')" [disabled]="!isDiagnosticAvailable('link_flaps') || running || reviewLoading"><mat-icon>sync_problem</mat-icon>LinkFlaps</button>
            </div>
          </details>

          <details class="support-accordion">
            <summary><span><mat-icon>build_circle</mat-icon><strong>Routine maintenance</strong><small>Service refreshes that can briefly affect traffic. Review shows the exact commands first.</small></span><span class="risk-badge risk-badge--medium">Medium to high</span></summary>
            <div class="action-list">
              <article class="action-row" *ngFor="let tool of networkRoutineActions" [class.action-row--unavailable]="!tool.available">
                <mat-icon class="action-row__icon">{{ toolIcon(tool.id) }}</mat-icon>
                <div><strong>{{ tool.name }}</strong><p>{{ tool.description }}</p><small *ngIf="tool.expected_downtime">Expected interruption: {{ tool.expected_downtime }}</small><small *ngIf="!tool.available">This action is not available until its device command is confirmed.</small></div>
                <span class="risk-badge" [class.risk-badge--medium]="tool.risk_level === 'medium'" [class.risk-badge--high]="tool.risk_level === 'high'">{{ tool.risk_level }}</span>
                <button mat-raised-button class="action-review-button" (click)="reviewTool(tool)" [disabled]="!tool.available || running"><mat-icon>{{ tool.available ? 'fact_check' : 'block' }}</mat-icon>{{ tool.available ? 'Review action' : 'Unavailable' }}</button>
              </article>
            </div>
          </details>

          <details class="support-accordion support-accordion--danger">
            <summary><span><mat-icon>warning</mat-icon><strong>High-impact actions</strong><small>These actions may interrupt customer traffic or disconnect the device.</small></span><span class="risk-badge risk-badge--high">High impact</span></summary>
            <div class="action-list">
              <article class="action-row" *ngFor="let tool of networkHighImpactActions" [class.action-row--unavailable]="!tool.available">
                <mat-icon class="action-row__icon">{{ toolIcon(tool.id) }}</mat-icon>
                <div><strong>{{ tool.name }}</strong><p>{{ tool.description }}</p><small *ngIf="tool.expected_downtime">Expected interruption: {{ tool.expected_downtime }}</small><small *ngIf="!tool.available">This action is not available until its device command is confirmed.</small></div>
                <span class="risk-badge risk-badge--high">{{ tool.risk_level }}</span>
                <button mat-raised-button class="action-review-button action-review-button--danger" (click)="reviewTool(tool)" [disabled]="!tool.available || running"><mat-icon>{{ tool.available ? 'fact_check' : 'block' }}</mat-icon>{{ tool.available ? 'Review action' : 'Unavailable' }}</button>
              </article>
            </div>
          </details>
        </section>

        <section class="interface-snapshot" *ngIf="showDiagnosticsTelemetry">
          <header class="interface-snapshot__header">
            <div>
              <span class="eyebrow">Device telemetry</span>
              <h4>Interfaces and SD-WAN links</h4>
              <p *ngIf="diagnosticsCapturedAt">Snapshot {{ diagnosticsCapturedAt | date:'medium' }}</p>
            </div>
            <button mat-icon-button matTooltip="Refresh interface snapshot" aria-label="Refresh interface snapshot"
              (click)="refreshDiagnosticsSnapshot()" [disabled]="catalogRefreshing || running">
              <mat-icon [class.refresh-spin]="catalogRefreshing">refresh</mat-icon>
            </button>
          </header>

          <div class="snapshot-summary">
            <span><strong>{{ diagnosticsRows.length }}</strong> interfaces</span>
            <span class="snapshot-summary--up"><strong>{{ diagnosticsUpCount }}</strong> up</span>
            <span class="snapshot-summary--down"><strong>{{ diagnosticsDownCount }}</strong> down</span>
            <span><strong>{{ formatBytes(diagnosticsTotalUsageBytes) }}</strong> total usage</span>
            <span class="snapshot-stale" *ngIf="diagnosticsSnapshotStale"><mat-icon>schedule</mat-icon>Telemetry stale</span>
          </div>

          <div class="interface-table-wrap" *ngIf="diagnosticsRows.length; else noDiagnosticsRows">
            <table class="interface-table">
              <thead><tr><th>Zone</th><th>State</th><th>SD-WAN</th><th>Type</th><th>Network</th><th>Device</th><th>Protocol</th><th>MAC</th><th>Address</th><th>External IP</th><th>ISP</th><th>Action</th><th>Uptime</th><th>Usage</th></tr></thead>
              <tbody>
                <tr *ngFor="let row of diagnosticsRows; trackBy: trackDiagnosticsRow">
                  <td><span class="zone-label" [ngClass]="'zone-label--' + row.zone">{{ row.zone }}</span></td>
                  <td><span class="interface-state" [class.interface-state--down]="row.state === 'down'"><span></span>{{ row.state | titlecase }}</span></td>
                  <td>{{ row.sdwan || '—' }}</td><td>{{ row.type || '—' }}</td><td>{{ row.network || '—' }}</td><td>{{ row.device || '—' }}</td><td>{{ row.protocol || '—' }}</td>
                  <td class="mono-cell">{{ row.mac || '—' }}</td><td class="mono-cell">{{ row.address || '—' }}</td><td class="mono-cell">{{ row.external_ip || '—' }}</td>
                  <td class="isp-cell">{{ row.isp || '—' }}</td><td>{{ row.action || '—' }}</td><td>{{ formatUptime(row.uptime_seconds) }}</td><td>{{ formatBytes(row.usage_bytes) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <ng-template #noDiagnosticsRows><div class="snapshot-empty"><mat-icon>lan</mat-icon><span>No interface telemetry is available in the latest device record.</span></div></ng-template>
        </section>

      </div>
    </mat-tab>

    <mat-tab label="4GLTE">
      <div class="tab-content lte-page">

        <details class="support-accordion lte-device-details" open>
          <summary>
            <span><mat-icon>sim_card</mat-icon><strong>4GLTE Device Details</strong><small>Current cellular configuration and SIM information reported by this device.</small></span>
            <span class="risk-badge risk-badge--low">Read-only</span>
          </summary>
          <dl class="lte-details-grid">
            <div *ngFor="let detail of lteDeviceDetails">
              <dt>{{ detail.label }}</dt>
              <dd [class.lte-details-grid__value--masked]="detail.label === 'Password'">{{ detail.value }}</dd>
            </div>
          </dl>
        </details>

        <details class="support-accordion lte-carrier-connect" open>
          <summary>
            <span><mat-icon>signal_cellular_alt</mat-icon><strong>Switch Cellular Carrier</strong><small>Connect to another available carrier.</small></span>
            <span class="risk-badge risk-badge--high">Connection change</span>
          </summary>
          <div class="carrier-connect-content">
            <header class="carrier-connect-heading">
              <div><h5>Connect to different available carrier</h5></div>
              <span class="carrier-market-context"><mat-icon>signal_cellular_alt</mat-icon><span><small>Current carrier</small><strong>{{ currentLteCarrierLabel }}</strong></span></span>
              <span class="carrier-market-context"><mat-icon>location_on</mat-icon><span><small>Site country</small><strong>{{ lteCarrierCountryLabel }}</strong></span></span>
              <!-- <span class="carrier-selection-status" [class.carrier-selection-status--ready]="selectedLteCarrier"><mat-icon>{{ selectedLteCarrier ? 'check_circle' : 'info' }}</mat-icon>{{ !selectedLteCarrier ? 'Selection required' : (lteCarrierChangePending ? selectedLteCarrierLabel + ' selected' : selectedLteCarrierLabel + ' is current') }}</span> -->
            </header>

            <div class="carrier-sim-warning" *ngIf="!isRabbitRunMultiCarrierSim" role="status">
              <mat-icon>warning_amber</mat-icon>
              <span><strong>RabbitRun Multi-Carrier SIM not Detected.</strong> You may select a known carrier for this custom SIM.</span>
            </div>

            <div class="carrier-options" role="radiogroup" [attr.aria-label]="'SIM network carrier for ' + lteCarrierCountryLabel">
              <button type="button" class="carrier-option" *ngFor="let carrier of lteCarrierOptions"
                role="radio"
                [class.carrier-option--selected]="selectedLteCarrier === carrier.value"
                [attr.aria-checked]="selectedLteCarrier === carrier.value"
                [disabled]="running || reviewLoading"
                (click)="selectLteCarrier(carrier.value)">
                <mat-icon>{{ selectedLteCarrier === carrier.value ? 'radio_button_checked' : 'radio_button_unchecked' }}</mat-icon>
                <span class="carrier-option__logo"><img *ngIf="carrier.logo" [src]="carrier.logo" [alt]="carrier.label + ' logo'"></span>
                <span class="carrier-option__copy"><strong>{{ carrier.label }}</strong><small>{{ carrier.helper }}</small></span>
              </button>
            </div>

            <div class="carrier-connect-summary" *ngIf="lteCarrierChangePending">
              <mat-icon>settings_input_antenna</mat-icon>
              <div><strong>Ready to review {{ selectedLteCarrierLabel }}</strong><span>The portal will save the carrier, reconnect 4GLTE, and collect connection logs for up to 20 seconds.</span></div>
            </div>

            <!-- <div class="carrier-connect-summary carrier-connect-summary--current" *ngIf="selectedLteCarrier && !lteCarrierChangePending">
              <mat-icon>check_circle</mat-icon>
              <div><strong>Currently connected to {{ selectedLteCarrierLabel }}</strong><span>Choose another carrier only when the installed SIM or network service requires it.</span></div>
            </div> -->

            <div class="carrier-connect-warning" *ngIf="lteCarrierChangePending"><mat-icon>warning_amber</mat-icon><span><strong>Expect a brief cellular interruption.</strong> This changes only the carrier selection. It does not change the APN, SIM credentials, or other modem settings.</span></div>

            <footer class="carrier-connect-actions">
              <span *ngIf="!selectedLteCarrier">Select one carrier to continue.</span>
              <span *ngIf="selectedLteCarrier && !lteCarrierChangePending">Choose a different carrier to continue.</span>
              <button mat-raised-button class="action-review-button" type="button" (click)="reviewLteCarrierConnection()"
                [disabled]="!lteCarrierChangePending || !lteCarrierConnectTool?.available || running || reviewLoading">
                <mat-icon>fact_check</mat-icon>{{ reviewLoading ? 'Preparing review...' : 'Review and connect' }}
              </button>
            </footer>
          </div>
        </details>

        <details class="support-accordion lte-modem-utilities" >
          <summary><span><mat-icon>settings_input_antenna</mat-icon><strong>4GLTE Modem Utilities</strong><small>Check modem health or review a modem operation. Disruptive actions show their impact before they run.</small></span><span class="risk-badge risk-badge--medium">Mixed impact</span></summary>
          <div class="lte-utility-content">
            <div class="lte-utility-row">
            <button mat-raised-button class="lte-utility-button lte-utility-button--danger" matTooltip="Power off the cellular modem. 4GLTE remains unavailable until it is turned on again." (click)="runLteUtility(lteModemOffTool)" [disabled]="!lteModemOffTool?.available || running || reviewLoading"><mat-icon>power_off</mat-icon>Switch Off 4G</button>
            <button mat-raised-button class="lte-utility-button" matTooltip="Unload the active SIM profile. Cellular service is unavailable until the SIM is loaded again." (click)="runLteUtility(lteSimUnloadTool)" [disabled]="!lteSimUnloadTool?.available || running || reviewLoading"><mat-icon>sim_card</mat-icon>Unload SIM</button>
            <button mat-raised-button class="lte-utility-button" matTooltip="Reset the cellular modem and wait for it to initialize again." (click)="runLteUtility(lteModemResetTool)" [disabled]="!lteModemResetTool?.available || running || reviewLoading"><mat-icon>refresh</mat-icon>Reset Modem</button>
            <button mat-raised-button class="lte-utility-button" matTooltip="Load the configured SIM profile into the cellular modem." (click)="runLteUtility(lteSimLoadTool)" [disabled]="!lteSimLoadTool?.available || running || reviewLoading"><mat-icon>sim_card</mat-icon>Load SIM</button>
            <button mat-raised-button class="lte-utility-button" matTooltip="Read the active SIM information without changing modem state." (click)="runLteUtility(lteSimReadTool)" [disabled]="!lteSimReadTool?.available || running || reviewLoading"><mat-icon>contactless</mat-icon>Read SIM</button>
            <button mat-raised-button class="lte-utility-button" matTooltip="Scan for cellular carriers visible to the modem. This may take about 90 seconds." (click)="runLteUtility(lteCarrierScanTool)" [disabled]="!lteCarrierScanTool?.available || running || reviewLoading"><mat-icon>network_check</mat-icon>Scan Carriers</button>
            <button mat-raised-button class="lte-utility-button" matTooltip="Read the modem manufacturer, model and installed firmware version." (click)="runLteUtility(lteModemVersionTool)" [disabled]="!lteModemVersionTool?.available || running || reviewLoading"><mat-icon>developer_board</mat-icon>Modem Version</button>
            <button mat-raised-button class="lte-utility-button" matTooltip="Check that at least 110 MB of memory is available before a modem firmware upgrade." (click)="runLteUtility(lteFirmwareMemoryTool)" [disabled]="!lteFirmwareMemoryTool?.available || running || reviewLoading"><mat-icon>memory</mat-icon>Check Free Memory</button>
            </div>
          </div>
        </details>

        <details class="support-accordion lte-settings" >
          <summary><span><mat-icon>settings_input_antenna</mat-icon><strong>Link2 4GLTE protection settings</strong><small>Control backup-link safeguards. Review shows the current state, proposed state, exact commands and rollback before applying.</small></span><span class="risk-badge risk-badge--high">Configuration</span></summary>
          <div class="config-list">
            <article class="config-row" *ngFor="let tool of lteConfigurationTools">
              <mat-icon class="action-row__icon">{{ toolIcon(tool.id) }}</mat-icon>
              <div>
                <strong>{{ tool.name }}</strong><p>{{ tool.description }}</p>
                <small *ngIf="tool.id === 'lte_safeguard'">Enable for a Link2 cellular backup to rebuild QoS safeguard rules after applying.</small>
                <small *ngIf="tool.id === 'carrier_switch'">Uses probe health to switch carriers. Enabling sets a 7200-second flap period and a minimum MOS of 1.30.</small>
                <small>Rollback: restore the previous enabled state and reset SD-WAN QoS.</small>
              </div>
              <div class="before-after"><span>Current <strong>{{ currentConfigurationValues[tool.id] ? 'Enabled' : 'Disabled' }}</strong></span><mat-icon>arrow_forward</mat-icon><span>Proposed <strong>{{ configurationValues[tool.id] ? 'Enabled' : 'Disabled' }}</strong></span></div>
              <mat-slide-toggle [(ngModel)]="configurationValues[tool.id]" [aria-label]="tool.name"></mat-slide-toggle>
              <button mat-raised-button class="action-review-button" (click)="reviewConfiguration(tool)" [disabled]="!tool.available || running"><mat-icon>fact_check</mat-icon>Review change</button>
            </article>
          </div>
        </details>
      </div>
    </mat-tab>

    <mat-tab label="SIPWatch">
      <ng-template matTabContent>
        <app-device-sipwatch-watch-list [device]="device"></app-device-sipwatch-watch-list>
      </ng-template>
    </mat-tab>

    <mat-tab label="Diagnostic Checks" *ngIf="showAdditionalDiagnostics">
      <div class="tab-content additional-diagnostics">
        <div class="section-heading diagnostics-toolbar">
          <div><h4>Diagnostic checks</h4><p>Select multiple checks and review the generated command batch before running it.</p></div>
          <mat-form-field appearance="outline" class="search-field"><mat-label>Search checks</mat-label><mat-icon matPrefix>search</mat-icon><input matInput [(ngModel)]="diagnosticSearch" (ngModelChange)="refreshDiagnosticGroups()"></mat-form-field>
        </div>
        <section class="diagnostic-settings" *ngIf="selectedDiagnosticCount">
          <header>
            <div><span class="eyebrow">Step 1</span><h5>Set test options</h5><p>These values are used only by the selected checks shown below.</p></div>
            <span class="settings-ready" [class.settings-ready--invalid]="!diagnosticSelectionValid"><mat-icon>{{ diagnosticSelectionValid ? 'check_circle' : 'error_outline' }}</mat-icon>{{ diagnosticSelectionValid ? 'Ready' : 'Input required' }}</span>
          </header>
          <div class="diagnostic-settings__fields">
            <mat-form-field appearance="outline" *ngIf="selectedDiagnosticsRequireTarget">
              <mat-label>Destination to test</mat-label>
              <mat-icon matPrefix class="setting-field-icon">public</mat-icon>
              <input matInput [(ngModel)]="diagnosticsTarget" placeholder="8.8.8.8" required pattern="[A-Za-z0-9.-]+">
              <mat-hint>Used by {{ selectedToolsUsing('target') }}</mat-hint>
              <mat-error>Enter a hostname or IPv4 address.</mat-error>
            </mat-form-field>
            <mat-form-field appearance="outline" *ngIf="selectedDiagnosticsRequireInterface">
              <mat-label>Connection to test</mat-label>
              <mat-icon matPrefix class="setting-field-icon">settings_ethernet</mat-icon>
              <mat-select [(ngModel)]="selectedInterface" required>
                <mat-option *ngFor="let item of supportInterfaces" [value]="item.name">{{ item.label }}<span *ngIf="item.address"> — {{ item.address }}</span></mat-option>
              </mat-select>
              <mat-hint>Used by {{ selectedToolsUsing('interface') }}</mat-hint>
            </mat-form-field>
          </div>
          <div class="diagnostic-settings__advanced" *ngIf="mtrSelected || speedTestSelected">
            <div class="diagnostic-option" *ngIf="mtrSelected">
              <div><strong>MTR options</strong><small>Controls how long the route analysis runs.</small></div>
              <mat-form-field appearance="outline"><mat-label>Reports</mat-label><input matInput type="number" min="1" max="20" [(ngModel)]="mtrCount"></mat-form-field>
              <mat-form-field appearance="outline"><mat-label>Interval</mat-label><mat-select [(ngModel)]="mtrInterval"><mat-option *ngFor="let value of [1,2,3,4,5,6,7,8,9,10]" [value]="value">{{ value }} second{{ value === 1 ? '' : 's' }}</mat-option></mat-select></mat-form-field>
              <mat-checkbox [(ngModel)]="mtrResolveNames">Show hostnames</mat-checkbox>
            </div>
            <div class="diagnostic-option" *ngIf="speedTestSelected">
              <div><strong>Speedtest options</strong><small>Choose the connection profile and measurement direction.</small></div>
              <mat-form-field appearance="outline"><mat-label>Connection type</mat-label><mat-select [(ngModel)]="speedConnectionType"><mat-option value="auto">Auto detect</mat-option><mat-option value="cellular">Cellular</mat-option><mat-option value="narrowband">Narrowband</mat-option><mat-option value="broadband">Broadband</mat-option><mat-option value="fiber">Fiber</mat-option></mat-select></mat-form-field>
              <div class="diagnostic-mode" role="group" aria-label="Speedtest direction"><button type="button" [class.diagnostic-mode--active]="speedMode === 'both'" (click)="setSpeedMode('both')">Both</button><button type="button" [class.diagnostic-mode--active]="speedMode === 'download'" (click)="setSpeedMode('download')">Download</button><button type="button" [class.diagnostic-mode--active]="speedMode === 'upload'" (click)="setSpeedMode('upload')">Upload</button></div>
            </div>
          </div>
        </section>
        <div class="diagnostic-list-heading" *ngIf="selectedDiagnosticCount"><span class="eyebrow">Step 2</span><strong>Choose checks</strong><span>Change a selection at any time.</span></div>
        <mat-accordion class="diagnostic-accordion" multi>
          <mat-expansion-panel *ngFor="let group of diagnosticGroups" [expanded]="group.name === 'Connectivity'">
            <mat-expansion-panel-header>
              <mat-panel-title>{{ group.name }}</mat-panel-title>
              <mat-panel-description>{{ group.tools.length }} available</mat-panel-description>
            </mat-expansion-panel-header>
            <div class="tool-list tool-list--grid">
              <article class="tool-row" *ngFor="let tool of group.tools" [class.tool-row--selected]="tool.selected">
                <mat-checkbox [(ngModel)]="tool.selected" (ngModelChange)="refreshSelectionState()" [disabled]="running || !tool.available" [aria-label]="tool.name"></mat-checkbox>
                <div class="tool-row__content"><div class="tool-row__title"><strong>{{ tool.name }}</strong><span class="duration"><mat-icon>schedule</mat-icon>{{ tool.expected_duration_seconds }}s</span></div><p>{{ tool.description }}</p><button type="button" class="technical-toggle" (click)="toggleTechnical(tool, $event)">{{ tool.technicalOpen ? 'Hide' : 'Show' }} command<mat-icon>{{ tool.technicalOpen ? 'expand_less' : 'expand_more' }}</mat-icon></button><code *ngIf="tool.technicalOpen">{{ commandSummary(tool) }}</code></div>
              </article>
            </div>
          </mat-expansion-panel>
        </mat-accordion>
        <div class="diagnostic-run-bar">
          <span><strong>{{ selectedDiagnosticCount }}</strong> selected <span aria-hidden="true">&middot;</span> about {{ selectedEstimatedDuration }}s</span>
          <div><button mat-button (click)="clearDiagnostics()" [disabled]="!selectedDiagnosticCount || running">Clear</button><button mat-raised-button color="primary" (click)="reviewDiagnostics()" [disabled]="running || reviewLoading || !diagnosticSelectionValid"><mat-icon>play_arrow</mat-icon>Run selected</button></div>
        </div>
      </div>
    </mat-tab>

    <mat-tab label="Service Controls" *ngIf="showServiceActionsTab">
      <div class="tab-content">
        <div class="section-heading">
          <div><h4>Operational actions</h4><p>Impact and downtime are shown before any action can be submitted.</p></div>
        </div>
        <section class="action-section">
          <header><h5>Low impact</h5><p>May briefly refresh device services.</p></header>
          <div class="action-list">
            <article class="action-row" *ngFor="let tool of lowImpactActions" [class.action-row--unavailable]="!tool.available">
              <div><strong>{{ tool.name }}</strong><p>{{ tool.description }}</p><small *ngIf="tool.expected_downtime">Downtime: {{ tool.expected_downtime }}</small></div>
              <span class="risk-badge" [class.risk-badge--medium]="tool.risk_level === 'medium'">{{ tool.risk_level }}</span>
              <button mat-raised-button class="action-review-button" (click)="reviewTool(tool)" [disabled]="!tool.available || running"><mat-icon>fact_check</mat-icon>Review</button>
            </article>
          </div>
        </section>
        <section class="action-section action-section--danger">
          <header><h5>High impact</h5><p>May interrupt customer traffic or device connectivity.</p></header>
          <div class="action-list">
            <article class="action-row" *ngFor="let tool of highImpactActions" [class.action-row--unavailable]="!tool.available">
              <div>
                <strong>{{ tool.name }}</strong><p>{{ tool.description }}</p>
                <small *ngIf="tool.expected_downtime">Expected downtime: {{ tool.expected_downtime }}</small>
                <small *ngIf="!tool.available">Device command mapping pending: {{ tool.backend_action }}</small>
              </div>
              <span class="risk-badge risk-badge--high">{{ tool.risk_level }}</span>
              <button mat-raised-button class="action-review-button action-review-button--danger" (click)="reviewTool(tool)" [disabled]="!tool.available || running"><mat-icon>fact_check</mat-icon>Review</button>
            </article>
          </div>
        </section>
      </div>
    </mat-tab>

    <mat-tab label="Configuration" *ngIf="showConfigurationTab">
      <div class="tab-content">
        <div class="section-heading"><div><h4>Approved configuration</h4><p>Review proposed values and rollback information before applying.</p></div></div>
        <div class="config-list">
          <article class="config-row" *ngFor="let tool of configurationTools">
            <div>
              <strong>{{ tool.name }}</strong><p>{{ tool.description }}</p>
              <small>Rollback: apply the previous enabled state and reset SD-WAN QoS.</small>
            </div>
            <div class="before-after"><span>Current <strong>{{ currentConfigurationValues[tool.id] ? 'Enabled' : 'Disabled' }}</strong></span><mat-icon>arrow_forward</mat-icon><span>Proposed <strong>{{ configurationValues[tool.id] ? 'Enabled' : 'Disabled' }}</strong></span></div>
            <mat-slide-toggle [(ngModel)]="configurationValues[tool.id]" [aria-label]="tool.name"></mat-slide-toggle>
            <button mat-stroked-button color="primary" (click)="reviewConfiguration(tool)" [disabled]="running">Review change</button>
          </article>
        </div>

      </div>
    </mat-tab>

    <mat-tab label="LAN Jump" *ngIf="showLanJumpTab">
      <div class="tab-content lanjump-page">
        <section class="lanjump-panel" *ngIf="lanJumpTool">
          <header class="lanjump-heading">
            <div>
              <span class="eyebrow">Temporary LAN access</span>
              <h4>LAN Jump</h4>
              <p>Confirm that the router can reach a LAN device before opening a temporary support connection.</p>
            </div>
            <span class="risk-badge risk-badge--high">Service restart</span>
          </header>

          <section class="lanjump-table-card">
            <div class="lanjump-table-toolbar">
              <div>
                <h5>Jump to LAN (HTTP/HTTPS)</h5>
                <p>Select a row, test the LAN device, then save and apply that session.</p>
              </div>
              <button type="button" mat-stroked-button color="primary" (click)="discoverLanJumpDevices()"
                [disabled]="running || lanJumpDiscoveryState === 'loading' || !lanJumpDiscoveryTool?.available">
                <mat-spinner *ngIf="lanJumpDiscoveryState === 'loading'" diameter="16"></mat-spinner>
                <mat-icon *ngIf="lanJumpDiscoveryState !== 'loading'">refresh</mat-icon>
                {{ lanJumpDiscoveryState === 'loading' ? 'Finding devices...' : 'Refresh device list' }}
              </button>
            </div>

            <div class="lanjump-discovery-message"
              [class.lanjump-discovery-message--error]="lanJumpDiscoveryState === 'failed'">
              <mat-icon>{{ lanJumpDiscoveryState === 'failed' ? 'error_outline' : 'info' }}</mat-icon>
              <span>{{ lanJumpDiscoveryMessage }}</span>
            </div>

            <div class="lanjump-table-wrap">
              <table class="lanjump-table">
                <thead><tr>
                  <th>Jump session</th><th>Description</th><th>Destination device</th><th>Destination port</th>
                  <th>Protocol</th><th>Expire</th><th>Test status</th><th>Enable</th><th>Status</th>
                </tr></thead>
                <tbody>
                  <tr *ngFor="let session of lanJumpSessions" (click)="selectLanJumpSession(session)"
                    [class.lanjump-row--active]="lanJump === session">
                    <td><button type="button" class="lanjump-session-button" (click)="selectLanJumpSession(session)">
                      <mat-icon>{{ lanJump === session ? 'radio_button_checked' : 'radio_button_unchecked' }}</mat-icon>
                      LANDevice{{ session.slot }}
                    </button></td>
                    <td><input class="lanjump-cell-input" [(ngModel)]="session.description" maxlength="40"
                      (focus)="selectLanJumpSession(session)" aria-label="Description"></td>
                    <td>
                      <div class="lanjump-destination-control">
                        <mat-icon class="lanjump-destination-search-icon">search</mat-icon>
                        <input class="lanjump-cell-input" [ngModel]="session.target"
                          (ngModelChange)="updateLanJumpTarget(session, $event)"
                          [matAutocomplete]="lanDeviceAutocomplete"
                          placeholder="Search device or enter IP"
                          aria-label="Search detected LAN devices or enter an IPv4 address">
                        <mat-autocomplete #lanDeviceAutocomplete="matAutocomplete"
                          class="lanjump-destination-panel"
                          panelWidth="420px"
                          (optionSelected)="chooseLanJumpDestination(session, $event.option.value)">
                          <mat-option *ngFor="let destination of filteredLanJumpDestinations(session.target)"
                            [value]="destination.ip">
                            <span class="lanjump-destination-option">
                              <mat-icon>{{ destination.is_sip_phone ? 'phone_in_talk' : 'devices' }}</mat-icon>
                              <span>
                                <strong>{{ lanJumpDestinationTitle(destination) }}</strong>
                                <small>{{ lanJumpDestinationDetails(destination) }}</small>
                              </span>
                            </span>
                          </mat-option>
                          <mat-option disabled *ngIf="lanJumpDestinations.length && !filteredLanJumpDestinations(session.target).length">
                            No matching detected device. You can still enter the IPv4 address manually.
                          </mat-option>
                        </mat-autocomplete>
                      </div>
                    </td>
                    <td><input class="lanjump-cell-input lanjump-port-input" type="number" min="1" max="65535"
                      [ngModel]="session.port" (ngModelChange)="updateLanJumpPort(session, $event)" aria-label="Destination port"></td>
                    <td><select class="lanjump-cell-select" [ngModel]="session.protocol"
                      (ngModelChange)="updateLanJumpProtocol(session, $event)" aria-label="Protocol">
                      <option value="https">HTTPS</option><option value="http">HTTP</option>
                    </select></td>
                    <td><select class="lanjump-cell-select" [ngModel]="session.expiration"
                      (ngModelChange)="updateLanJumpExpiration(session, $event)" aria-label="Expiration">
                      <option [ngValue]="15">15 mins</option><option [ngValue]="30">30 mins</option>
                      <option [ngValue]="60">60 mins</option><option [ngValue]="120">120 mins</option>
                    </select></td>
                    <td><span class="lanjump-table-status lanjump-table-status--{{ lanJump === session ? lanJumpTestState : 'idle' }}">
                      {{ lanJumpSessionTestStatus(session) }}
                    </span></td>
                    <td class="lanjump-enable-cell"><mat-checkbox [checked]="session.enabled"
                      (change)="updateLanJumpEnabled(session, $event.checked)" [disabled]="running || reviewLoading"
                      aria-label="Enable temporary LAN access"></mat-checkbox></td>
                    <td><span class="lanjump-device-status" [class.lanjump-device-status--active]="session.activeOnDevice">
                      {{ lanJumpSessionStatus(session) }}
                    </span></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="lanjump-selected-summary">
              <mat-icon>touch_app</mat-icon>
              <span>Selected: <strong>LANDevice{{ lanJump.slot }}</strong> - {{ lanJump.protocol | uppercase }}
                {{ lanJump.target || 'destination not set' }}:{{ lanJump.port }} - {{ lanJump.expiration }} minutes</span>
            </div>

            <div class="lanjump-actions">
              <div class="lanjump-test-status lanjump-test-status--{{ lanJumpInlineStatusState }}" role="status" aria-live="polite">
                <mat-spinner *ngIf="lanJumpOperationInProgress" diameter="16"></mat-spinner>
                <mat-icon *ngIf="!lanJumpOperationInProgress">
                  {{ lanJumpInlineStatusState === 'active' || lanJumpTestState === 'passed' ? 'check_circle' : lanJumpTestState === 'failed' ? 'error' : 'info' }}
                </mat-icon>
                <span>{{ lanJumpInlineStatusMessage }}</span>
              </div>
              <button type="button" mat-raised-button class="lanjump-test-button" (click)="testLanJumpTarget()"
                [disabled]="!canTestLanJump">
                <mat-spinner *ngIf="lanJumpTestState === 'testing'" diameter="17"></mat-spinner>
                <mat-icon *ngIf="lanJumpTestState !== 'testing'">network_check</mat-icon>
                {{ lanJumpTestState === 'testing' ? 'Testing...' : 'Test LAN Device' }}
              </button>
              <button type="button" mat-raised-button class="lanjump-apply-button" (click)="reviewLanJump()"
                [disabled]="!canReviewLanJump">
                <mat-icon>fact_check</mat-icon>Save &amp; Apply
              </button>
            </div>
            <p class="lanjump-review-hint" [class.lanjump-ready]="canReviewLanJump">
              <mat-icon>{{ canReviewLanJump ? 'check_circle' : 'info' }}</mat-icon>{{ lanJumpReviewHint }}
            </p>
          </section>
        </section>
      </div>
    </mat-tab>

    <mat-tab label="Advanced Commands" *ngIf="canUseAdvancedCommands">
      <div class="tab-content">
        <div class="action-list">
          <article class="action-row" *ngFor="let tool of advancedActions" [class.action-row--unavailable]="!tool.available">
            <div><strong>{{ tool.name }}</strong><p>{{ tool.description }}</p></div>
            <span class="risk-badge risk-badge--high">{{ tool.risk_level }}</span>
            <button mat-raised-button class="action-review-button action-review-button--danger" (click)="reviewTool(tool)" [disabled]="!tool.available || running">
              <mat-icon>{{ tool.available ? 'fact_check' : 'block' }}</mat-icon>{{ tool.available ? 'Review action' : 'Unavailable' }}
            </button>
          </article>
        </div>
        <div class="advanced-warning"><mat-icon>admin_panel_settings</mat-icon><div><strong>Administrator-only CLI</strong><p>Prefer approved support tools. Raw commands receive syntax screening and a final review, but can still change device state.</p></div></div>
        <div class="section-heading">
          <div><h4>Paste reviewed commands</h4><p>Comments and blank lines are removed before review.</p></div>
          <button mat-icon-button matTooltip="Clear commands" aria-label="Clear commands" (click)="clearCommands()" [disabled]="running || !rawCommands"><mat-icon>delete_sweep</mat-icon></button>
        </div>
        <mat-form-field appearance="outline" class="command-input">
          <mat-label>CLI commands</mat-label>
          <textarea matInput [(ngModel)]="rawCommands" (ngModelChange)="onCommandsChanged()" [disabled]="running" rows="12" spellcheck="false" placeholder="# Paste commands here"></textarea>
        </mat-form-field>
        <div class="validation-summary" *ngIf="rawCommands">
          <span class="validation-summary__valid"><mat-icon>check_circle</mat-icon>{{ validCommands.length }} ready</span>
          <span class="validation-summary__rejected" *ngIf="rejectedCommands.length"><mat-icon>error</mat-icon>{{ rejectedCommands.length }} excluded</span>
        </div>
        <div class="rejected-list" *ngIf="rejectedCommands.length">
          <div *ngFor="let item of rejectedCommands"><strong>Line {{ item.line }}</strong><code>{{ item.command }}</code><span>{{ item.reason }}</span></div>
        </div>
        <div class="action-bar"><span>{{ validCommands.length ? 'Commands are validated again by the device service.' : 'Add at least one valid command.' }}</span><button mat-raised-button color="primary" (click)="reviewConfigCommands()" [disabled]="running || !validCommands.length"><mat-icon>fact_check</mat-icon>Review and apply</button></div>
      </div>
    </mat-tab>

    <mat-tab label="Activity History">
      <div class="tab-content">
        <div class="section-heading"><div><h4>Session run history</h4><p>Runs completed during this browser session. Device operations are also written to the backend audit log.</p></div></div>
        <div class="empty-state" *ngIf="!runHistory.length"><mat-icon>history</mat-icon><strong>No runs in this session</strong><span>Completed diagnostics and actions will appear here.</span></div>
        <div class="history-table" *ngIf="runHistory.length">
          <div class="history-row history-row--header"><span>Date</span><span>Operation</span><span>Result</span><span>Runtime</span><span></span></div>
          <div class="history-row" *ngFor="let item of runHistory"><span>{{ item.startedAt | date:'short' }}</span><strong>{{ item.label }}</strong><span [class.status-success]="item.status === 'Completed'" [class.status-failed]="item.status === 'Failed'">{{ item.status }}</span><span>{{ item.runtimeMs / 1000 | number:'1.1-1' }}s</span><button mat-icon-button matTooltip="View output" aria-label="View run output" (click)="rerun(item)"><mat-icon>visibility</mat-icon></button></div>
        </div>
      </div>
    </mat-tab>
  </mat-tab-group>

  <p class="inline-error" *ngIf="reviewError" role="alert"><mat-icon>error_outline</mat-icon>{{ reviewError }}</p>

  <ng-container *ngTemplateOutlet="executionOutput"></ng-container>
</section>

<ng-template #executionOutput>
  <section class="execution-panel" [class.execution-panel--diagnostic]="diagnosticOutput"
    *ngIf="executionState !== 'idle' && activeTab === outputTabIndex && !suppressExecutionOutput">
    <header>
      <div><mat-spinner *ngIf="running" diameter="18"></mat-spinner><mat-icon *ngIf="executionState === 'completed'" class="status-success">check_circle</mat-icon><mat-icon *ngIf="executionState === 'accepted'" class="status-accepted">schedule</mat-icon><mat-icon *ngIf="executionState === 'failed'" class="status-failed">error</mat-icon><strong>{{ executionStatusText }}</strong></div>
      <div class="output-actions" *ngIf="!running"><button mat-icon-button matTooltip="Copy ticket summary" aria-label="Copy ticket summary" (click)="copyTicketSummary()"><mat-icon>content_copy</mat-icon></button><button mat-icon-button matTooltip="Export output" aria-label="Export output" (click)="exportOutput()"><mat-icon>download</mat-icon></button></div>
    </header>
    <section class="structured-result" *ngIf="speedResult && !running">
      <div><span>Ping</span><strong>{{ speedResult.ping !== null ? (speedResult.ping | number:'1.0-1') + ' ms' : '—' }}</strong></div>
      <div><span>Jitter</span><strong>{{ speedResult.jitter !== null ? (speedResult.jitter | number:'1.0-1') + ' ms' : '—' }}</strong></div>
      <div><span>Download</span><strong>{{ speedResult.download_mbit !== null ? (speedResult.download_mbit | number:'1.1-2') + ' Mbit/s' : '—' }}</strong></div>
      <div><span>Upload</span><strong>{{ speedResult.upload_mbit !== null ? (speedResult.upload_mbit | number:'1.1-2') + ' Mbit/s' : '—' }}</strong></div>
      <a *ngIf="speedResultShareUrl" class="speed-share-result" [href]="speedResultShareUrl" target="_blank" rel="noopener noreferrer">
        <img [src]="speedResultShareUrl" alt="Shared speed test result">
        <span>Open shared result <mat-icon>open_in_new</mat-icon></span>
      </a>
    </section>
    <section class="memory-readiness" *ngIf="modemMemoryResult && !running" [class.memory-readiness--failed]="!modemMemoryResult.ready">
      <mat-icon>{{ modemMemoryResult.ready ? 'check_circle' : 'error' }}</mat-icon>
      <div><strong>{{ modemMemoryResult.ready ? 'Ready for modem firmware upgrade' : 'Not enough memory for modem firmware upgrade' }}</strong><span>{{ modemMemoryResult.availableMb }} MB available; {{ modemMemoryResult.requiredMb }} MB required.</span><span *ngIf="!modemMemoryResult.ready">- Reboot the device and try again.</span></div>
    </section>
    <p class="diagnostic-progress-note" *ngIf="diagnosticOutput && polling"><mat-icon>schedule</mat-icon>This diagnostic can take up to {{ activeRunExpectedSeconds || 45 }} seconds. You can stay on this page while the result is collected.</p>
    <pre *ngIf="!running"><ng-container *ngFor="let segment of outputSegments"><a *ngIf="segment.url" [href]="segment.url" target="_blank" rel="noopener noreferrer">{{ segment.text }}</a><ng-container *ngIf="!segment.url">{{ segment.text }}</ng-container></ng-container></pre>
    <p class="waiting-copy" *ngIf="running">Final output will appear after the command batch finishes.</p>
    <footer *ngIf="responseMeta && !running"><span *ngIf="responseMeta.command_count !== undefined">{{ responseMeta.command_count }} command{{ responseMeta.command_count === 1 ? '' : 's' }}</span><span *ngIf="responseMeta.exit_code !== undefined">Exit code {{ responseMeta.exit_code }}</span><span *ngIf="responseMeta.elapsed_ms !== undefined">{{ responseMeta.elapsed_ms / 1000 | number:'1.1-1' }}s</span><span *ngIf="responseMeta.operation_id">Operation {{ responseMeta.operation_id }}</span></footer>
  </section>
</ng-template>

<ng-template #mtrInputDialog>
  <div class="diagnostic-input-dialog">
    <header mat-dialog-title><mat-icon>route</mat-icon><div><h3>MTR path analysis</h3><p>Select the source and destination for the route test.</p></div></header>
    <div mat-dialog-content>
      <mat-form-field appearance="outline">
        <mat-label>Source interface</mat-label>
        <mat-select [(ngModel)]="selectedInterface">
          <mat-option *ngFor="let item of supportInterfaces" [value]="item.name">{{ item.label }}<span *ngIf="item.address"> — {{ item.address }}</span></mat-option>
        </mat-select>
      </mat-form-field>
      <mat-form-field appearance="outline">
        <mat-label>Destination</mat-label>
        <input matInput [(ngModel)]="diagnosticsTarget" placeholder="8.8.8.8">
        <mat-hint>Hostname or IPv4 address</mat-hint>
      </mat-form-field>
      <p class="duration-notice"><mat-icon>schedule</mat-icon>This test may take about 45 seconds.</p>
    </div>
    <div mat-dialog-actions align="end"><button mat-button (click)="cancelParameterDialog()">Cancel</button><button mat-raised-button class="utility-command-button" (click)="submitMtrInputs()" [disabled]="!selectedInterface || !diagnosticsTarget.match('^[A-Za-z0-9.-]+$')"><mat-icon>play_arrow</mat-icon>Continue</button></div>
  </div>
</ng-template>

<ng-template #speedTestInputDialog>
  <div class="diagnostic-input-dialog">
    <header mat-dialog-title><mat-icon>speed</mat-icon><div><h3>Speedtest</h3><p>Choose the source, connection profile, and test direction.</p></div></header>
    <div mat-dialog-content>
      <mat-form-field appearance="outline">
        <mat-label>Source interface</mat-label>
        <mat-select [(ngModel)]="selectedInterface">
          <mat-option *ngFor="let item of supportInterfaces" [value]="item.name">{{ item.label }}<span *ngIf="item.address"> — {{ item.address }}</span></mat-option>
        </mat-select>
      </mat-form-field>
      <mat-form-field appearance="outline">
        <mat-label>Connection type</mat-label>
        <mat-select [(ngModel)]="speedConnectionType">
          <mat-option value="auto">Auto</mat-option><mat-option value="cellular">Cellular</mat-option><mat-option value="narrowband">Narrowband</mat-option><mat-option value="broadband">Broadband</mat-option><mat-option value="fiber">Fiber</mat-option>
        </mat-select>
      </mat-form-field>
      <div class="speed-direction" role="group" aria-label="Speed test direction">
        <span>Speed</span>
        <button type="button" mat-raised-button [class.speed-direction--active]="speedMode === 'both'" (click)="setSpeedMode('both')"><mat-icon>swap_vert</mat-icon>Both</button>
        <button type="button" mat-raised-button [class.speed-direction--active]="speedMode === 'download'" (click)="setSpeedMode('download')"><mat-icon>south</mat-icon>Download</button>
        <button type="button" mat-raised-button [class.speed-direction--active]="speedMode === 'upload'" (click)="setSpeedMode('upload')"><mat-icon>north</mat-icon>Upload</button>
      </div>
    </div>
    <div mat-dialog-actions align="end"><button mat-button (click)="cancelParameterDialog()">Cancel</button><button mat-raised-button class="utility-command-button" (click)="submitSpeedTestInputs()" [disabled]="!selectedInterface"><mat-icon>play_arrow</mat-icon>Continue</button></div>
  </div>
</ng-template>

<ng-template #ifstatInputDialog>
  <div class="diagnostic-input-dialog">
    <header mat-dialog-title><mat-icon>show_chart</mat-icon><div><h3>Interface statistics</h3><p>Choose which interfaces to sample and how long to collect throughput.</p></div></header>
    <div mat-dialog-content>
      <mat-form-field appearance="outline">
        <mat-label>Interface group</mat-label>
        <mat-select [(ngModel)]="ifstatGroup">
          <mat-option value="sdwan">SD-WAN links</mat-option>
          <mat-option value="lan">LAN interfaces</mat-option>
        </mat-select>
        <mat-hint>The device resolves the active interfaces in this group.</mat-hint>
      </mat-form-field>

      <div class="ifstat-scope">
        <mat-icon>settings_ethernet</mat-icon>
        <div>
          <strong>{{ ifstatGroupLabel }}</strong>
          <span *ngIf="ifstatInterfaceLabels.length; else deviceResolvedIfstatInterfaces">{{ ifstatInterfaceLabels.join(', ') }}</span>
          <ng-template #deviceResolvedIfstatInterfaces><span>Active interfaces will be resolved on the device.</span></ng-template>
        </div>
      </div>

      <div class="capture-duration" role="group" aria-label="Interface statistics capture duration">
        <span>Capture duration</span>
        <button type="button" mat-raised-button [class.capture-duration--active]="ifstatDuration === 5" (click)="setIfstatDuration(5)"><mat-icon>timer</mat-icon>5 seconds</button>
        <button type="button" mat-raised-button [class.capture-duration--active]="ifstatDuration === 10" (click)="setIfstatDuration(10)"><mat-icon>timer</mat-icon>10 seconds</button>
      </div>

      <p class="duration-notice"><mat-icon>info</mat-icon>The device discovers the active interfaces first, then records one sample per second for the selected duration. Setup may take a few additional seconds. An existing IfStats capture is stopped before the new one starts.</p>
    </div>
    <div mat-dialog-actions align="end"><button mat-button (click)="cancelParameterDialog()">Cancel</button><button mat-raised-button class="utility-command-button" (click)="submitIfstatInputs()"><mat-icon>play_arrow</mat-icon>Start capture</button></div>
  </div>
</ng-template>

<ng-template #commandReviewDialog>
  <div class="review-dialog">
    <header mat-dialog-title><mat-icon>{{ reviewRisk === 'read-only' ? 'fact_check' : 'warning' }}</mat-icon><div><h3>{{ reviewTitle }}</h3><p>{{ deviceName }} <span aria-hidden="true">&middot;</span> {{ deviceMac | uppercase }}</p></div><span class="risk-badge" [class.risk-badge--readonly]="reviewRisk === 'read-only'" [class.risk-badge--high]="reviewRisk === 'high' || reviewRisk === 'critical'">{{ reviewRisk }}</span></header>
    <div mat-dialog-content>
      <div class="review-summary">
        <span><mat-icon>format_list_numbered</mat-icon>{{ showReviewCommands ? reviewCommands.length : reviewOperations.length }} {{ showReviewCommands ? 'commands' : 'operation' }}{{ !showReviewCommands && reviewOperations.length === 1 ? '' : 's' }}</span>
        <span><mat-icon>schedule</mat-icon>About {{ reviewDurationSeconds }}s</span>
      </div>
      <ng-container *ngIf="showReviewCommands; else supportOperationSummary">
        <p>The following commands will run on the selected device in this order.</p>
        <ol class="review-list"><li *ngFor="let command of reviewDisplayCommands"><code>{{ command }}</code></li></ol>
      </ng-container>
      <ng-template #supportOperationSummary>
        <p>Confirm to run this operation on the selected device. Its result will appear here when it completes.</p>
      </ng-template>
      <div class="review-excluded" *ngIf="reviewRejected.length"><strong>{{ reviewRejected.length }} line{{ reviewRejected.length === 1 ? '' : 's' }} will not be sent</strong><span *ngFor="let item of reviewRejected">Line {{ item.line }}: {{ item.reason }}</span></div>

    </div>
    <div mat-dialog-actions align="end"><button mat-button (click)="cancelReview()">Cancel</button><button mat-raised-button [color]="reviewRisk === 'critical' ? 'warn' : 'primary'" (click)="confirmExecution()" [disabled]="!canProceedReview" cdkFocusInitial><mat-icon>play_arrow</mat-icon>{{ reviewType === 'advanced' || reviewRisk !== 'read-only' ? 'Execute' : 'Run diagnostics' }}</button></div>
  </div>
</ng-template>
